import { useEffect, useState, useCallback } from "react";
import { twilioApi } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MessageSquare, Send, CheckCircle2, AlertCircle, Phone } from "lucide-react";

export default function TwilioWire() {
  const { user, patchUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [phone, setPhone] = useState(user?.phone_number || "");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([twilioApi.status(), twilioApi.deliveries()]);
      setStatus(s);
      setDeliveries(d);
    } catch (e) { console.error("twilio wire load failed", e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => { setPhone(user?.phone_number || ""); }, [user?.phone_number]);

  const savePhone = async () => {
    if (!phone.trim()) { toast.error("Enter a phone number"); return; }
    setSaving(true);
    try {
      const u = await twilioApi.setPhone(phone.trim());
      patchUser({ phone_number: u.phone_number });
      toast.success("Phone number saved");
      load();
    } catch (e) { console.error("save phone failed", e); toast.error("Failed to save phone"); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await twilioApi.test();
      if (r.status === "sent") toast.success(`SMS queued (Twilio ${r.twilio_status || "ok"})`);
      else if (r.status === "skipped") toast.warning(`Skipped: ${r.error}`);
      else toast.error(`Failed: ${r.error}`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Test failed";
      toast.error(detail);
    } finally { setTesting(false); }
  };

  const StatusRow = ({ label, ok, hint }) => (
    <div className="flex items-center justify-between text-[11px] font-mono-uc" data-testid={`twilio-status-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <span className="text-white/60 uppercase tracking-wider">{label}</span>
      <span className={`flex items-center gap-1 ${ok ? "text-emerald-400" : "text-amber-400"}`}>
        {ok ? <CheckCircle2 size={11}/> : <AlertCircle size={11}/>}
        {ok ? "ready" : (hint || "pending")}
      </span>
    </div>
  );

  return (
    <section className="uc-card p-6 uc-fade" data-testid="twilio-wire">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="uc-chip mb-2"><MessageSquare size={11}/> TWILIO WIRE · SMS ALERTS</div>
          <h2 className="font-serif-display text-2xl">SMS Notifications</h2>
          <p className="text-white/50 text-xs mt-1 max-w-xl">
            Controller trips, red inbox events, Dead Man&apos;s Switch nudges, and approved charges
            get pushed to your phone the moment they fire. All alerts remain in-app when Twilio isn&apos;t wired.
          </p>
        </div>
        <Button
          data-testid="twilio-test-btn"
          onClick={sendTest}
          disabled={testing || !status?.user_phone_configured}
          className="uc-btn-amber rounded-none h-9"
        >
          <Send size={12} className="mr-1"/> {testing ? "Sending…" : "Send Test SMS"}
        </Button>
      </div>

      {!loading && status && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4">
          <div className="p-3 border border-white/10 space-y-2">
            <StatusRow label="SDK" ok={status.sdk_installed}/>
            <StatusRow label="Account" ok={status.account_configured} hint="creds missing"/>
          </div>
          <div className="p-3 border border-white/10 space-y-2">
            <StatusRow label="From Number" ok={status.from_number_configured} hint="waiting on compliance"/>
            <StatusRow label="Your Phone" ok={status.user_phone_configured} hint="not set"/>
          </div>
          <div className={`p-3 border md:col-span-2 flex items-center justify-between ${status.ready ? "border-emerald-500/40 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5"}`}>
            <span className="font-mono-uc text-[11px] uppercase tracking-widest text-white/70">Wire Status</span>
            <span className={`font-serif-display text-2xl ${status.ready ? "text-emerald-400" : "text-amber-400"}`} data-testid="twilio-wire-status">
              {status.ready ? "LIVE" : "STANDBY"}
            </span>
          </div>
        </div>
      )}

      <div className="p-3 border border-white/10 mb-4">
        <label className="font-mono-uc text-[10px] uppercase tracking-widest text-white/50 block mb-2 flex items-center gap-1">
          <Phone size={11}/> Commander Phone (E.164 preferred: +14155551234)
        </label>
        <div className="flex gap-2">
          <Input
            data-testid="twilio-phone-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 415 555 1234"
            className="h-10 rounded-none bg-[#121212] border-white/10 font-mono-uc"
          />
          <Button
            data-testid="twilio-phone-save"
            onClick={savePhone}
            disabled={saving}
            className="rounded-none h-10 px-4 font-mono-uc text-[10px] uppercase tracking-wider border border-white/20 bg-transparent hover:bg-white/5"
            variant="ghost"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <div>
        <div className="font-mono-uc text-[10px] uppercase tracking-widest text-white/50 mb-2">Recent Deliveries · {deliveries.length}</div>
        {deliveries.length === 0 ? (
          <div className="text-[11px] text-white/40 italic py-2 border border-dashed border-white/10 text-center">
            No SMS attempts yet. Tap &quot;Send Test SMS&quot; to smoke-test the wire.
          </div>
        ) : (
          <div className="space-y-1 max-h-56 overflow-y-auto" data-testid="twilio-deliveries-list">
            {deliveries.slice(0, 15).map((d) => {
              const badge = d.status === "sent" ? "text-emerald-400" : d.status === "failed" ? "text-red-400" : "text-amber-400";
              return (
                <div key={d.id} className="p-2 border border-white/10 flex items-start gap-2 font-mono-uc text-[11px]" data-testid={`twilio-delivery-${d.id}`}>
                  <span className={`uppercase tracking-widest text-[10px] flex-none ${badge}`}>{d.status}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-white/70 truncate">{d.body_preview}</div>
                    <div className="text-white/40 text-[10px] mt-0.5 flex justify-between">
                      <span>{d.phone || "no phone"}</span>
                      <span>{new Date(d.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                    {d.error && <div className="text-red-300 text-[10px] mt-0.5">{d.error}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
